import { formatMoney } from './format';

/** Должно совпадать с backend/src/bidding/bid-limits.ts */
export const MAX_BID_START_PRICE_MULTIPLIER = 1000;
export const MAX_BID_STEP = 200_000;

export function computeMinRequiredBid(auction: {
  bids_count: number;
  start_price: number;
  current_price: number;
  min_step: number;
}): number {
  if (auction.bids_count === 0) {
    return auction.start_price;
  }
  return auction.current_price + auction.min_step;
}

export function computeAbsoluteMaxAllowedBid(auction: {
  start_price: number;
  min_step: number;
  buy_now_price: number | null;
}): number {
  let max = auction.start_price * MAX_BID_START_PRICE_MULTIPLIER;

  if (auction.buy_now_price != null) {
    const buyNowCap = auction.buy_now_price;
    if (buyNowCap > 0 && buyNowCap < max) {
      max = buyNowCap;
    }
  }

  return max;
}

/** Максимум одной ручной ставки на лоте. */
export function computeMaxManualBid(auction: {
  bids_count: number;
  start_price: number;
  current_price: number;
  min_step: number;
  buy_now_price: number | null;
}): number {
  const absolute = computeAbsoluteMaxAllowedBid(auction);
  const incrementCap = computeMinRequiredBid(auction) + MAX_BID_STEP;
  return Math.min(absolute, incrementCap);
}

/** Максимум автоставки (без лимита прироста за один шаг). */
export function computeMaxAutoBidAmount(auction: {
  start_price: number;
  min_step: number;
  buy_now_price: number | null;
}): number {
  return computeAbsoluteMaxAllowedBid(auction);
}

export function getMaxAllowedBid(auction: {
  bids_count: number;
  start_price: number;
  current_price: number;
  min_step: number;
  buy_now_price: number | null;
}): number {
  return computeMaxManualBid(auction);
}

export function getMaxAutoBidAmount(auction: {
  start_price: number;
  min_step: number;
  buy_now_price: number | null;
}): number {
  return computeMaxAutoBidAmount(auction);
}

export function exceedsBuyNowPrice(
  auction: { buy_now_price: number | null },
  amount: number,
): boolean {
  return auction.buy_now_price != null && amount > auction.buy_now_price;
}

export type ManualBidRange = {
  min: number;
  max: number;
  canBid: boolean;
  /** Следующая ставка по шагу уже выше цены мгновенного выкупа */
  stepExceedsBuyNow: boolean;
  /** Единственная допустимая ставка — цена выкупа */
  onlyBuyNowPrice: boolean;
};

export function getManualBidRange(auction: {
  bids_count: number;
  start_price: number;
  current_price: number;
  min_step: number;
  buy_now_price: number | null;
}): ManualBidRange {
  const min = computeMinRequiredBid(auction);
  const max = computeMaxManualBid(auction);

  if (auction.buy_now_price != null && min > auction.buy_now_price) {
    return {
      min,
      max,
      canBid: false,
      stepExceedsBuyNow: true,
      onlyBuyNowPrice: false,
    };
  }

  if (min > max) {
    return {
      min,
      max,
      canBid: false,
      stepExceedsBuyNow: false,
      onlyBuyNowPrice: false,
    };
  }

  const onlyBuyNowPrice =
    auction.buy_now_price != null &&
    min === max &&
    min === auction.buy_now_price;

  return {
    min,
    max,
    canBid: true,
    stepExceedsBuyNow: false,
    onlyBuyNowPrice,
  };
}

export function formatMaxBidExceededError(
  auction: {
    bids_count: number;
    start_price: number;
    current_price: number;
    min_step: number;
    buy_now_price: number | null;
  },
  bidAmount: number,
): string {
  if (exceedsBuyNowPrice(auction, bidAmount)) {
    return `Ставка выше цены мгновенного выкупа. Максимум — ${formatMoney(auction.buy_now_price!)}`;
  }
  return `Максимум ${formatMoney(computeMaxManualBid(auction))}`;
}

export function formatBidLimitsHint(
  auction: {
    bids_count: number;
    start_price: number;
    current_price: number;
    min_step: number;
    buy_now_price: number | null;
  },
  bidRange: ManualBidRange = getManualBidRange(auction),
): string {
  const { min, max, canBid, stepExceedsBuyNow, onlyBuyNowPrice } = bidRange;

  if (stepExceedsBuyNow && auction.buy_now_price != null) {
    return `Следующая ставка по шагу (${formatMoney(min)}) выше цены «Купить сразу» (${formatMoney(auction.buy_now_price)}). Используй выкуп.`;
  }

  if (!canBid) {
    return 'Сейчас нельзя сделать ставку в допустимом диапазоне';
  }

  if (onlyBuyNowPrice || min === max) {
    if (auction.buy_now_price != null && min === auction.buy_now_price) {
      return `Можно поставить только ${formatMoney(min)} — аукцион завершится по цене выкупа`;
    }
    return `Ставка — ${formatMoney(min)}`;
  }

  const range = `От ${formatMoney(min)} до ${formatMoney(max)}`;
  if (auction.buy_now_price != null) {
    return `${range}. Максимум — цена «Купить сразу» (${formatMoney(auction.buy_now_price)})`;
  }
  return range;
}

export function formatAutoBidLimitsHint(auction: {
  bids_count: number;
  start_price: number;
  current_price: number;
  min_step: number;
  buy_now_price: number | null;
}): string {
  const minMax = computeMinRequiredBid(auction);
  const maxAllowed = computeMaxAutoBidAmount(auction);

  if (auction.buy_now_price != null && minMax > auction.buy_now_price) {
    return `Автоставка недоступна: минимум ${formatMoney(minMax)} выше цены «Купить сразу» (${formatMoney(auction.buy_now_price)}). Используй выкуп.`;
  }

  if (minMax > maxAllowed) {
    return `Максимум автоставки — ${formatMoney(maxAllowed)}`;
  }

  if (minMax === maxAllowed) {
    if (auction.buy_now_price != null && minMax === auction.buy_now_price) {
      return `Можно указать только ${formatMoney(maxAllowed)} — аукцион завершится по цене выкупа`;
    }
    return `Максимум автоставки — ${formatMoney(maxAllowed)}`;
  }

  const range = `От ${formatMoney(minMax)} до ${formatMoney(maxAllowed)}`;
  if (auction.buy_now_price != null) {
    return `${range}. Не выше цены «Купить сразу» (${formatMoney(auction.buy_now_price)})`;
  }
  return range;
}
